import { pathify } from '@cims-online/shared'
import { Result, Space, Spin } from 'antd'
import type { ColumnsType } from 'antd/lib/table'
import RecordsTable from 'App/components/ProjectRoutes/components/customWorkflows/Records/components/RecordsTable'
import { useRecordsTable } from 'App/components/ProjectRoutes/components/customWorkflows/Records/hooks/useRecordsTable'
import type { WorkflowRecord } from 'App/components/ProjectRoutes/components/customWorkflows/Records/types'
import CIMSLink from 'components/CIMSLink'
import { useProjectContext } from 'context/ProjectContext'
import { useCallback, useMemo } from 'react'
import { useWorkflowPayItemSummaryTableQuery } from 'schema'
import { DefaultQueryHookOptions } from 'utils/defaults'

type WorkflowPayItemSummaryTableProps = {
  payItemGlobalId: string
  workflowId: string
}

const noActions = () => ({})
const noCommentCreation = async () => undefined

const WorkflowPayItemSummaryTable: React.FC<
  WorkflowPayItemSummaryTableProps
> = ({ payItemGlobalId, workflowId }) => {
  const { projectId } = useProjectContext()
  const { data, error, loading, refetch } = useWorkflowPayItemSummaryTableQuery(
    {
      ...DefaultQueryHookOptions,
      variables: { payItemGlobalId, workflowId },
    }
  )
  const workflow = data?.listWorkflows.records[0]
  const recordsPageName = workflow?.customizeRecordsLabel?.enabled
    ? workflow.customizeRecordsLabel.label
    : 'Records'
  const records = data?.payItemNoGeometry?.workflowRecords.records ?? []
  const commentActions = useMemo(
    () => ({ canCreate: false, onCommentAdded: noCommentCreation }),
    []
  )
  const table = useRecordsTable(
    workflow?.recordsTableConfig ?? {
      columns: [],
      expand: { commentFields: [], fieldIds: [] },
    },
    records,
    commentActions
  )
  const columns = useMemo<ColumnsType<WorkflowRecord>>(
    () => [
      ...table.columns,
      {
        key: 'record',
        render: (_value, record) => (
          <CIMSLink
            newTab
            to={pathify(
              projectId,
              'workflows',
              workflowId,
              'records',
              record.id
            )}
          >
            Open
          </CIMSLink>
        ),
        title: recordsPageName,
        width: 90,
      },
    ],
    [projectId, recordsPageName, table.columns, workflowId]
  )
  const reload = useCallback(() => refetch(), [refetch])

  if (error)
    return (
      <Result
        status="error"
        title="Unable to load workflow records"
        subTitle={error.message}
      />
    )
  if (!workflow && loading)
    return (
      <Space>
        <Spin />
        <span>Loading workflow records...</span>
      </Space>
    )
  if (!workflow) return <Result status="404" title="Workflow not found" />
  if (!data?.payItemNoGeometry)
    return <Result status="404" title="Pay Item not found" />

  return (
    <RecordsTable
      actions={noActions}
      columns={columns}
      expandable={table.expandable}
      key={`${workflowId}:${payItemGlobalId}`}
      loading={loading}
      onReload={reload}
      records={records}
    />
  )
}

export default WorkflowPayItemSummaryTable
